import type { ComponentType } from 'react'
import { goldReportNavSections } from '@proffer/shared'

export type NavTone =
  | 'violet'
  | 'emerald'
  | 'amber'
  | 'sky'
  | 'zinc'
  | 'bronze'
  | 'silver'
  | 'gold'

export type AppNavItem = {
  to: string
  label: string
  tone: NavTone
  end?: boolean
  packageGate?: 2 | 3 | 4
  isActive?: (pathname: string) => boolean
  Icon: ComponentType<{ className?: string }>
}

function IconHome({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  )
}

function IconDocument({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  )
}

function IconUsers({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766m-.001-.108a4.125 4.125 0 017.533-2.493M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  )
}

function IconClipboard({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.548-.08m-5.09 4.845v-.045a2.25 2.25 0 00-2.25-2.25h-2.25a2.25 2.25 0 00-2.25 2.25v.045m7.5 0v-.045a2.25 2.25 0 012.25-2.25h2.25a2.25 2.25 0 012.25 2.25v.045M12 8.25V6.108c0-1.135.845-2.098 1.976-2.192a48.424 48.424 0 011.548-.08" />
    </svg>
  )
}

function IconCalendar({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
    </svg>
  )
}

function IconFolder({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
    </svg>
  )
}

function IconStar({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
    </svg>
  )
}

export function IconWallet({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
    </svg>
  )
}

function IconBell({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
    </svg>
  )
}

function IconChart({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  )
}

export function IconCog({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

export function IconLogout({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9"
      />
    </svg>
  )
}

/** Χρώματα εικονιδίου header — ίδια τονικότητα με sidebar */
export const navHeaderIconTone: Record<NavTone, string> = {
  violet:
    'bg-violet-100 text-violet-700 ring-1 ring-violet-200/80 dark:bg-violet-950/60 dark:text-violet-300 dark:ring-violet-800/80',
  emerald:
    'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200/80 dark:bg-emerald-950/60 dark:text-emerald-300 dark:ring-emerald-800/80',
  amber:
    'bg-amber-100 text-amber-800 ring-1 ring-amber-200/80 dark:bg-amber-950/50 dark:text-amber-200 dark:ring-amber-800/80',
  sky: 'bg-sky-100 text-sky-700 ring-1 ring-sky-200/80 dark:bg-sky-950/60 dark:text-sky-300 dark:ring-sky-800/80',
  zinc: 'bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200/80 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-zinc-700/80',
  bronze:
    'bg-orange-100 text-orange-800 ring-1 ring-orange-200/80 dark:bg-orange-950/50 dark:text-orange-200 dark:ring-orange-800/80',
  silver:
    'bg-slate-100 text-slate-700 ring-1 ring-slate-200/80 dark:bg-slate-900/60 dark:text-slate-300 dark:ring-slate-700/80',
  gold:
    'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200/80 dark:bg-yellow-950/50 dark:text-yellow-200 dark:ring-yellow-800/80',
}

export function isOffersSectionActive(pathname: string): boolean {
  if (pathname === '/offers') return true
  if (!pathname.startsWith('/offers/')) return false
  const segment = pathname.slice('/offers/'.length).split('/')[0]
  return segment !== '' && segment !== 'new' && segment !== 'capture'
}

export function isCustomersSectionActive(pathname: string): boolean {
  if (pathname === '/customers') return true
  if (!pathname.startsWith('/customers/')) return false
  const segment = pathname.slice('/customers/'.length).split('/')[0]
  return segment !== '' && segment !== 'new' && segment !== 'tasks'
}

export type AppNavGroup = {
  id: string
  title: string
  items: AppNavItem[]
}

/** Στοιχείο οριζόντιου top menu (σύνδεσμος ή dropdown ομάδας). */
export type AppTopNavEntry =
  | { type: 'link'; item: AppNavItem }
  | {
      type: 'menu'
      id: string
      label: string
      tone: NavTone
      items: AppNavItem[]
    }

const topMenuGroupOrder = ['daily', 'more', 'reports'] as const

const topMenuGroupTone: Record<(typeof topMenuGroupOrder)[number], NavTone> = {
  daily: 'violet',
  more: 'zinc',
  reports: 'gold',
}

/** Ομάδες sidebar με κουμπί collapse (κενό = μόνο στατικός τίτλος, πάντα ανοιχτές). */
export const navGroupsCollapsedByDefault = new Set<string>()

/** Top menu: Αρχική + dropdown ανά ομάδα sidebar. */
export function buildAppTopNavEntries(
  homeItem: AppNavItem,
  groups: AppNavGroup[],
): AppTopNavEntry[] {
  const byId = new Map(groups.map((g) => [g.id, g]))
  const entries: AppTopNavEntry[] = [{ type: 'link', item: homeItem }]

  for (const groupId of topMenuGroupOrder) {
    const group = byId.get(groupId)
    if (!group || group.items.length === 0) continue
    entries.push({
      type: 'menu',
      id: group.id,
      label: group.title,
      tone: topMenuGroupTone[groupId],
      items: group.items,
    })
  }

  return entries
}

export function isTopNavMenuActive(
  items: AppNavItem[],
  pathname: string,
): boolean {
  return items.some((item) => isNavItemActive(item, pathname))
}

/** Αρχική — πάντα πάνω από τις ομάδες, χωρίς τίτλο κατηγορίας. */
export const appNavHomeItem: AppNavItem = {
  to: '/',
  label: 'Αρχική',
  tone: 'violet',
  end: true,
  Icon: IconHome,
}

/** Ομαδοποίηση sidebar ανά εργασία χρήστη (όχι ονόματα πακέτων). */
export const appNavGroups: AppNavGroup[] = [
  {
    id: 'daily',
    title: 'Καθημερινή εργασία',
    items: [
      {
        to: '/offers',
        label: 'Προσφορές',
        tone: 'violet',
        Icon: IconDocument,
        isActive: isOffersSectionActive,
      },
      {
        to: '/customers',
        label: 'Πελάτες',
        tone: 'emerald',
        Icon: IconUsers,
        isActive: isCustomersSectionActive,
      },
      { to: '/customers/tasks', label: 'Εργασίες', tone: 'sky', Icon: IconClipboard },
      { to: '/appointments', label: 'Ραντεβού', tone: 'amber', packageGate: 3, Icon: IconCalendar },
    ],
  },
  {
    id: 'more',
    title: 'Περισσότερα',
    items: [
      { to: '/projects', label: 'Έργα', tone: 'bronze', packageGate: 2, Icon: IconFolder },
      { to: '/reviews', label: 'Αξιολογήσεις', tone: 'bronze', packageGate: 2, Icon: IconStar },
      {
        to: '/notifications',
        label: 'Ειδοποιήσεις',
        tone: 'sky',
        packageGate: 3,
        Icon: IconBell,
      },
      { to: '/expenses', label: 'Έξοδα', tone: 'silver', packageGate: 3, Icon: IconWallet },
    ],
  },
  {
    id: 'reports',
    title: 'Αναφορές',
    items: goldReportNavSections.map((section) => ({
      to: section.path,
      label: section.label,
      tone: 'gold' as const,
      packageGate: 4 as const,
      end: section.id === 'statistics',
      isActive:
        section.id === 'statistics'
          ? (pathname: string) =>
              pathname === '/reports' || pathname === '/reports/'
          : (pathname: string) => pathname.startsWith(section.path),
      Icon: IconChart,
    })),
  },
]

const GATE_FEATURE: Record<2 | 3 | 4, string> = {
  2: 'public_profile',
  3: 'expenses',
  4: 'reports_nav_statistics',
}

function navItemVisible(
  item: AppNavItem,
  entitlements: string[],
): boolean {
  if (!item.packageGate) return true
  const feature = GATE_FEATURE[item.packageGate]
  return entitlements.includes(feature)
}

export function filterNavItems(
  items: AppNavItem[],
  entitlements: string[],
): AppNavItem[] {
  return items.filter((item) => navItemVisible(item, entitlements))
}

export function filterNavGroups(
  groups: AppNavGroup[],
  entitlements: string[],
): AppNavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: filterNavItems(group.items, entitlements),
    }))
    .filter((group) => group.items.length > 0)
}

function isNavItemActive(item: AppNavItem, pathname: string): boolean {
  if (item.isActive) return item.isActive(pathname)
  if (item.end) return pathname === item.to
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}

/** Τίτλος ομάδας sidebar· κενό για Αρχική ή routes εκτός ομάδων. */
export function getActiveNavGroupTitle(
  pathname: string,
  topItems: AppNavItem[],
  groups: AppNavGroup[],
): string {
  for (const item of topItems) {
    if (isNavItemActive(item, pathname)) return ''
  }
  for (const group of groups) {
    for (const item of group.items) {
      if (isNavItemActive(item, pathname)) return group.title
    }
  }
  for (const route of headerOnlyRoutes) {
    if (pathname === route.prefix || pathname.startsWith(`${route.prefix}/`)) {
      return route.groupTitle ?? ''
    }
  }
  return ''
}

const headerOnlyRoutes: {
  prefix: string
  label: string
  tone: NavTone
  Icon: AppNavItem['Icon']
  groupTitle?: string
}[] = [
  { prefix: '/settings', label: 'Ρυθμίσεις', tone: 'zinc', Icon: IconCog },
  {
    prefix: '/billing',
    label: 'Συνδρομή',
    tone: 'violet',
    Icon: IconWallet,
    groupTitle: 'Λογαριασμός',
  },
]

/** Ενεργό nav item (ίδιο με sidebar) για header / breadcrumbs */
export function getActiveNavItem(
  pathname: string,
  topItems: AppNavItem[],
  groups: AppNavGroup[],
): AppNavItem | null {
  for (const item of topItems) {
    if (isNavItemActive(item, pathname)) return item
  }
  for (const group of groups) {
    for (const item of group.items) {
      if (isNavItemActive(item, pathname)) return item
    }
  }
  for (const route of headerOnlyRoutes) {
    if (pathname === route.prefix || pathname.startsWith(`${route.prefix}/`)) {
      return {
        to: route.prefix,
        label: route.label,
        tone: route.tone,
        Icon: route.Icon,
        end: true,
      }
    }
  }
  return null
}

export function getActiveNavLabel(
  pathname: string,
  topItems: AppNavItem[],
  groups: AppNavGroup[],
): string {
  return getActiveNavItem(pathname, topItems, groups)?.label ?? ''
}
